// ABOUTME: Orchestration hook for the Delivery Window entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useDeliveryWindowConfig } from './useDeliveryWindowConfig'
import { useCreateDeliveryWindow, useCreateDeliveryWindowWithScope, useUpdateDeliveryWindow, useDeleteDeliveryWindow } from './useDeliveryWindowMutations'
import { DeliveryWindowConfig, DeliveryWindowFormValues, DeliveryWindowOverrides } from '../types/deliveryWindow'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseDeliveryWindowEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: DeliveryWindowFormValues) => void
}

interface UseDeliveryWindowEntityResult {
  config: DeliveryWindowConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

const equalsParent = (values: DeliveryWindowFormValues, config: DeliveryWindowConfig): boolean =>
  JSON.stringify(values) === JSON.stringify(config.parentValues)

// An empty timezone is never "overridden". Otherwise, keep an already-stored override
// (config.overrides.timeZone) even if the value happens to match the parent this session —
// that distinguishes a deliberate "same as parent" pick from an untouched inherited value.
const deriveTimeZoneOverride = (values: DeliveryWindowFormValues, config: DeliveryWindowConfig): boolean => {
  const trimmed = values.timeZone?.trim() ?? ''
  if (!trimmed) return false
  return config.overrides.timeZone || trimmed !== config.parentValues.timeZone
}

export const useDeliveryWindowEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseDeliveryWindowEntityParams): UseDeliveryWindowEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useDeliveryWindowConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    timeZone: cfg.timeZone,
    businessDays: cfg.businessDays,
    slotManagementDays: cfg.slotManagementDays,
    standardOperatingDays: cfg.standardOperatingDays,
    standardHours: cfg.standardHours,
    nonStandardHours: cfg.nonStandardHours,
    deliveryConstraints: cfg.deliveryConstraints,
  })), [rawConfig, pendingScope])

  const createMutation = useCreateDeliveryWindow(countryId, businessLine)
  const createWithScopeMutation = useCreateDeliveryWindowWithScope(countryId, businessLine)
  const updateMutation = useUpdateDeliveryWindow(countryId, businessLine)
  const deleteMutation = useDeleteDeliveryWindow(countryId, businessLine)

  const formValuesRef = useRef<DeliveryWindowFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<DeliveryWindowConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: DeliveryWindowFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !configRef.current) return
      const values = formValuesRef.current
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (equalsParent(values, configRef.current)) {
          onSuccess({})
          return
        }
        const overrides: DeliveryWindowOverrides = { timeZone: deriveTimeZoneOverride(values, configRef.current), businessDays: true, operatingSchedule: true, minDeliveryWindow: true, minFixDeliveryWindow: true, stepInterval: true }
        mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values, overrides }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (scopeId !== rootScopeId && equalsParent(values, configRef.current)) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      // Override-all: saving at any scope marks all sections as overridden (no partial section overrides yet)
      const overrides: DeliveryWindowOverrides = {
        timeZone: deriveTimeZoneOverride(values, configRef.current),
        businessDays: true, operatingSchedule: true, minDeliveryWindow: true, minFixDeliveryWindow: true, stepInterval: true
      }
      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values, overrides }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
  }
}
